import React, { useState, useEffect, useMemo } from 'react';
import { customersAPI } from '../services/api';
import CobranzaModal from '../components/CobranzaModal';
import Toast from '../components/Toast';
import './DebtsPage.css';

// ─────────────────────────────────────────
// Calcula el grupo de aging según la fecha
// más antigua de deuda del cliente
// ─────────────────────────────────────────
const getAgingGroup = (oldestDateStr) => {
    if (!oldestDateStr) return '+60';
    const days = Math.floor((Date.now() - new Date(oldestDateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 30) return '0-30';
    if (days <= 60) return '31-60';
    return '+60';
};

const agingLabel = { '0-30': '🟢 0–30 días', '31-60': '🟡 31–60 días', '+60': '🔴 +60 días' };
const agingColor = { '0-30': '#16a34a', '31-60': '#d97706', '+60': '#dc2626' };
const agingBg    = { '0-30': '#f0fff4', '31-60': '#fffbeb', '+60': '#fff5f5' };

const DebtsPage = () => {
    const [debtors, setDebtors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [agingFilter, setAgingFilter] = useState('');   // '' | '0-30' | '31-60' | '+60'
    const [selectedDebtor, setSelectedDebtor] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => setToast({ message, type });

    useEffect(() => { loadDebtors(); }, []);

    const loadDebtors = async () => {
        setIsLoading(true);
        try {
            const res = await customersAPI.getDebtors();
            setDebtors(res.data.data?.debtors || []);
        } catch {
            showToast('Error al cargar deudores', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // ── KPIs calculados ──
    const kpis = useMemo(() => {
        const total = debtors.reduce((sum, d) => sum + parseFloat(d.total_debt || 0), 0);
        const byGroup = { '0-30': 0, '31-60': 0, '+60': 0 };
        debtors.forEach(d => { byGroup[getAgingGroup(d.oldest_debt_date)] += parseFloat(d.total_debt || 0); });
        return { total, byGroup, count: debtors.length };
    }, [debtors]);

    // ── Filtrado ──
    const filtered = useMemo(() => {
        return debtors.filter(d => {
            const matchSearch = !searchTerm ||
                d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (d.phone || '').includes(searchTerm);
            const group = getAgingGroup(d.oldest_debt_date);
            const matchAging = !agingFilter || group === agingFilter;
            return matchSearch && matchAging;
        });
    }, [debtors, searchTerm, agingFilter]);

    const handlePaid = () => {
        loadDebtors();
        setSelectedDebtor(null);
    };

    return (
        <div className="content-pane">
            {/* ── Encabezado ── */}
            <div className="orders-header">
                <h2>💳 Cuentas por Cobrar</h2>
                <p className="orders-header-subtitle">Fiados y saldos pendientes de todos tus clientes</p>
            </div>

            {/* ── KPIs ── */}
            <div className="debts-kpis">
                <div className="debts-kpi-card debts-kpi-total">
                    <span className="debts-kpi-icon">💰</span>
                    <div>
                        <p className="debts-kpi-label">TOTAL POR COBRAR</p>
                        <h3 className="debts-kpi-value">S/. {kpis.total.toFixed(2)}</h3>
                        <small>{kpis.count} clientes con deuda</small>
                    </div>
                </div>

                {Object.entries(kpis.byGroup).map(([group, amount]) => (
                    <div
                        key={group}
                        className="debts-kpi-card debts-kpi-aging"
                        style={{ borderLeftColor: agingColor[group], cursor: 'pointer' }}
                        onClick={() => setAgingFilter(agingFilter === group ? '' : group)}
                    >
                        <div>
                            <p className="debts-kpi-label" style={{ color: agingColor[group] }}>
                                {agingLabel[group]}
                                {agingFilter === group && ' ✓'}
                            </p>
                            <h3 className="debts-kpi-value" style={{ color: agingColor[group] }}>
                                S/. {amount.toFixed(2)}
                            </h3>
                            <small>
                                {debtors.filter(d => getAgingGroup(d.oldest_debt_date) === group).length} clientes
                            </small>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Filtros ── */}
            <div className="filters-container" style={{ justifyContent: 'space-between' }}>
                <div className="search-container" style={{ margin: 0, flex: 1 }}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar por nombre o teléfono..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                {agingFilter && (
                    <button
                        onClick={() => setAgingFilter('')}
                        style={{
                            background: 'none', border: '1px solid #cbd5e1', borderRadius: 8,
                            padding: '8px 14px', cursor: 'pointer', color: '#64748b', fontSize: '0.88rem'
                        }}
                    >
                        ✕ Quitar filtro
                    </button>
                )}
            </div>

            {/* ── Tabla ── */}
            {isLoading ? (
                <div className="loading-container"><p>⏳ Cargando deudores...</p></div>
            ) : filtered.length === 0 ? (
                <div className="empty-container">
                    <p>{debtors.length === 0 ? '🎉 ¡No hay deudas pendientes!' : 'No hay resultados para el filtro seleccionado.'}</p>
                </div>
            ) : (
                <div className="table-container">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Teléfono</th>
                                <th style={{ textAlign: 'right', color: '#dc2626' }}>Deuda Total</th>
                                <th>Antigüedad</th>
                                <th>Crédito Límite</th>
                                <th className="center">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(debtor => {
                                const group = getAgingGroup(debtor.oldest_debt_date);
                                const debt  = parseFloat(debtor.total_debt || 0);
                                const limit = parseFloat(debtor.credit_limit || 0);
                                const daysOld = debtor.oldest_debt_date
                                    ? Math.floor((Date.now() - new Date(debtor.oldest_debt_date).getTime()) / 86400000)
                                    : null;

                                return (
                                    <tr key={debtor.id}>
                                        <td><strong>{debtor.name}</strong></td>
                                        <td>{debtor.phone || '—'}</td>
                                        <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 700, fontSize: '1rem' }}>
                                            S/. {debt.toFixed(2)}
                                        </td>
                                        <td>
                                            <span style={{
                                                background: agingBg[group],
                                                color: agingColor[group],
                                                padding: '4px 12px', borderRadius: 20,
                                                fontSize: '0.8rem', fontWeight: 600
                                            }}>
                                                {agingLabel[group]}
                                                {daysOld !== null && ` (${daysOld}d)`}
                                            </span>
                                        </td>
                                        <td>
                                            {limit > 0
                                                ? <span style={{ color: debt > limit ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                                                    S/. {limit.toFixed(2)} {debt > limit ? '⚠️' : '✓'}
                                                  </span>
                                                : <span style={{ color: '#94a3b8' }}>Sin límite</span>
                                            }
                                        </td>
                                        <td className="center">
                                            <button
                                                onClick={() => setSelectedDebtor({ ...debtor, total_debt: debt })}
                                                style={{
                                                    background: '#16a34a', color: '#fff',
                                                    border: 'none', borderRadius: 8,
                                                    padding: '6px 16px', cursor: 'pointer',
                                                    fontWeight: 600, fontSize: '0.85rem',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                💰 Cobrar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Modal de Cobro ── */}
            <CobranzaModal
                customer={selectedDebtor}
                onClose={() => setSelectedDebtor(null)}
                onPaid={handlePaid}
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default DebtsPage;
